import React from 'react';
import Slider from "react-slick";
import Select from 'react-select';
import SectionInfoBox from '../../../components/SectionInfoBox';

interface ReviewsSectionProps {
    reviews: any[];
    renderStars: (count: number) => React.ReactNode;
    project: any;
    showReviewForm: boolean;
    setShowReviewForm: (show: boolean) => void;
    reviewSuccess: string;
    reviewError: string;
    handleReviewSubmit: (e: React.FormEvent) => void;
    ratingOptions: any[];
    reviewForm: any;
    setReviewForm: React.Dispatch<React.SetStateAction<any>>;
    submitting: boolean;
}

const ReviewsSection: React.FC<ReviewsSectionProps> = ({
    reviews,
    renderStars,
    project,
    showReviewForm,
    setShowReviewForm,
    reviewSuccess,
    reviewError,
    handleReviewSubmit,
    ratingOptions,
    reviewForm,
    setReviewForm,
    submitting
}) => {
    return (
        <div id="reviews" className="card border border-gray-200 shadow-sm rounded-2xl mb-8 overflow-hidden">
            <div className="bg-white py-6 px-6 md:px-8 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center">
                    <h4 className="text-xl md:text-2xl mb-0 font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95]">User Reviews ({reviews.length})</h4>
                    <SectionInfoBox text={project.sectionInfo?.reviews} />
                </div>
                <button
                    onClick={() => setShowReviewForm(!showReviewForm)}
                    className="inline-block text-center cursor-pointer transition-all bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] px-6 py-2 rounded-full font-black shadow-lg text-[11px] md:text-[12px] active:scale-95 border border-[#f1ae24]/30"
                >
                    {showReviewForm ? 'Cancel Review' : 'Write a Review'}
                </button>
            </div>
            <div className="p-6 md:p-8">
                {reviews.length > 0 ? (
                    <div className="reviews-slider-container relative">
                        <Slider {...{
                            dots: true,
                            infinite: false,
                            speed: 500,
                            slidesToShow: reviews.length >= 2 ? 2 : 1,
                            slidesToScroll: 1,
                            autoplay: false,
                            arrows: false,
                            responsive: [
                                {
                                    breakpoint: 768,
                                    settings: {
                                        slidesToShow: 1,
                                        slidesToScroll: 1
                                    }
                                }
                            ]
                        }}>
                            {reviews.map((rev, idx) => (
                                <div key={rev._id || idx} className="px-3">
                                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm h-full flex flex-col">
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className="bg-[#05144b] bg-opacity-10 text-[#05144b] rounded-full flex items-center justify-center font-black" style={{ width: '50px', height: '50px', flexShrink: 0, fontSize: '20px' }}>
                                                {rev.name?.charAt(0)?.toUpperCase() || 'U'}
                                            </div>
                                            <div className="flex-grow">
                                                <h5 className="text-[15px] md:text-[17px] font-bold text-black mb-0">{rev.name}</h5>
                                                <div className="flex items-center gap-1">
                                                    {renderStars(rev.rating)}
                                                </div>
                                            </div>
                                            <span className="text-black font-medium text-[12px] whitespace-nowrap">
                                                {new Date(rev.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                            </span>
                                        </div>
                                        <p className="text-black mb-0 leading-relaxed font-medium relative" style={{ fontSize: '15px' }}>
                                            <i className="fa-solid fa-quote-left mr-2 opacity-10"></i>
                                            {rev.message}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                        <style>{`
                            .reviews-slider-container .slick-dots {
                                bottom: -40px;
                            }
                            .reviews-slider-container .slick-dots li button:before {
                                font-size: 10px;
                                color: #05144b;
                            }
                            .reviews-slider-container .slick-dots li.slick-active button:before {
                                color: #05144b;
                            }
                            .reviews-slider-container .slick-list {
                                margin: 0 -12px;
                                padding-bottom: 20px;
                            }
                        `}</style>
                    </div>
                ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-2xl mb-8 border-dashed border-gray-200">
                        <i className="fa-regular fa-comment-dots fa-3x text-gray-500 opacity-25 mb-6"></i>
                        <h5 className="text-lg font-bold text-gray-900">No reviews yet</h5>
                        <p className="text-gray-500 text-sm mb-0">Be the first to share your thoughts about {project.name}!</p>
                    </div>
                )}

                {/* Write Review Form */}
                {showReviewForm && (
                    <div id="review-form" className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                        <h5 className="text-lg font-black mb-6 text-[#05144b]"><i className="fa-solid fa-pen-to-square mr-2 text-[#f1ae24]"></i>Write a Review</h5>

                        {reviewSuccess && (
                            <div className="alert alert-success border-gray-200 shadow-sm rounded-lg py-2 mb-6 flex items-center gap-2">
                                <i className="fa-solid fa-circle-check"></i>{reviewSuccess}
                            </div>
                        )}
                        {reviewError && (
                            <div className="alert alert-danger border-gray-200 shadow-sm rounded-lg py-2 mb-6 flex items-center gap-2">
                                <i className="fa-solid fa-circle-exclamation"></i>{reviewError}
                            </div>
                        )}

                        <form onSubmit={handleReviewSubmit}>
                            <div className="flex flex-wrap -mx-3">
                                <div className="w-full px-6">
                                    <div className="form-group mb-0">
                                        <label className="form-label text-[12px] md:text-[13px] font-bold text-black">Your Rating <span className="text-red-500">*</span></label>
                                        <Select
                                            options={ratingOptions}
                                            placeholder="Select Rating"
                                            className="review-select"
                                            required
                                            value={reviewForm.rating ? ratingOptions.find((opt: any) => opt.value === reviewForm.rating) : null}
                                            onChange={(opt: any) => setReviewForm((prev: any) => ({ ...prev, rating: opt?.value || '' }))}
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    borderRadius: '10px',
                                                    border: '1px solid #dee2e6',
                                                    padding: '2px',
                                                    boxShadow: 'none',
                                                    '&:hover': { borderColor: '#007bff' }
                                                })
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="w-full md:w-1/2 px-6">
                                    <div className="form-group mb-0">
                                        <label className="form-label text-[12px] md:text-[13px] font-bold text-black">Your Name <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control rounded-lg border-gray-200"
                                            placeholder="John Doe"
                                            required
                                            value={reviewForm.name}
                                            onChange={e => setReviewForm((prev: any) => ({ ...prev, name: e.target.value }))}
                                        />
                                    </div>
                                </div>
                                <div className="w-full md:w-1/2 px-6">
                                    <div className="form-group mb-0">
                                        <label className="form-label text-[12px] md:text-[13px] font-bold text-black">Email Address <span className="text-red-500">*</span></label>
                                        <input
                                            type="email"
                                            className="form-control rounded-lg border-gray-200"
                                            placeholder="john@example.com"
                                            required
                                            value={reviewForm.email}
                                            onChange={e => setReviewForm((prev: any) => ({ ...prev, email: e.target.value }))}
                                        />
                                    </div>
                                </div>
                                <div className="w-full px-6">
                                    <div className="form-group mb-0">
                                        <label className="form-label text-[12px] md:text-[13px] font-bold text-black">Your Feedback <span className="text-red-500">*</span></label>
                                        <textarea
                                            className="form-control rounded-lg border-gray-200"
                                            rows={4}
                                            placeholder="Share your experience with this project..."
                                            required
                                            value={reviewForm.message}
                                            onChange={e => setReviewForm((prev: any) => ({ ...prev, message: e.target.value }))}
                                        />
                                    </div>
                                </div>
                                <div className="w-full px-6 text-right">
                                    <button
                                        type="submit"
                                        className="inline-block text-center cursor-pointer transition-all bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] px-8 py-2.5 font-black rounded-full shadow-lg text-[12px] active:scale-95 border border-[#f1ae24]/30"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <><span className="spinner-border spinner-border-sm mr-2"></span>Posting...</>
                                        ) : 'Post Review'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewsSection;
